import React, { useMemo, useState } from "react";
import PermissionChildActionRow from "./PermissionChildActionRow/PermissionChildActionRow";
import { PermissionAction } from "../../../interfaces";
import { useRolePermissionsContext } from "../../../context/RolePermissionsContext";
import PermissionScope from "./PermissionChildActionRow/PermissionScope/PermissionScope";
import { RoleDetailsPageService } from "../../../service/RoleDetailsPageService";
import { TooltipCustom } from "@services/ui-components";

type PermissionActionRowProps = {
    action: PermissionAction;
    moduleCode: string;
};

const PermissionActionRow: React.FC<PermissionActionRowProps> = ({ action, moduleCode }) => {
    const actionId = `${action.code}-${action.order}`;
    const { label, isActive, children = [], supportsScope, scope } = action;
    const hasChildren = children.length > 0;
    const [isOpen, setIsOpen] = useState(hasChildren);

    const { onChangePermissionAction, permissions, mode } = useRolePermissionsContext();
    const isViewMode = mode === "view";
    const showScope = (isViewMode && supportsScope && isActive && scope) || (!isViewMode && supportsScope && scope);

    const isDisabled = useMemo(() => {
        if (!permissions) {
            return false;
        }

        return !RoleDetailsPageService.isActionDependenciesSatisfied(permissions.modules, action);
    }, [permissions, action]);

    return (
        <>
            <div className="p-2">
                <div className="d-flex align-items-center">
                    {hasChildren ? (
                        <button
                            type="button"
                            className="btn btn-sm p-0 border-0 bg-transparent shadow-none me-2"
                            onClick={() => setIsOpen((prev) => !prev)}
                        >
                            <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} fs-7`} />
                        </button>
                    ) : (
                        <span className="d-inline-block me-2" style={{ width: 16, height: 16 }} />
                    )}

                    <div className={`${!isViewMode ? "form-check" : ""} mb-0 d-flex align-items-start`}>
                        {isViewMode ? (
                            <>
                                {isActive ? (
                                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                                ) : (
                                    <i className="bi bi-x-circle-fill text-orange me-2"></i>
                                )}

                                <p className="form-check-label mb-0 fw-bold">
                                    {label}
                                    {action.descriptionInfo && (
                                        <TooltipCustom placement="right" text={action.descriptionInfo}>
                                            <i className="bi bi-info-circle fs-normal ms-2 i-to-block"></i>
                                        </TooltipCustom>
                                    )}
                                </p>
                            </>
                        ) : (
                            <>
                                <input
                                    id={actionId}
                                    checked={isActive}
                                    disabled={isDisabled}
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    onChange={(e) =>
                                        onChangePermissionAction(moduleCode, action.code, e.target.checked)
                                    }
                                />

                                <label htmlFor={actionId} className="form-check-label mb-0">
                                    <span className="fw-bold">{label}</span>
                                    {action.descriptionInfo && (
                                        <TooltipCustom placement="right" text={action.descriptionInfo}>
                                            <i className="bi bi-info-circle fs-normal ms-2 i-to-block"></i>
                                        </TooltipCustom>
                                    )}
                                </label>
                            </>
                        )}
                    </div>
                </div>

                {isOpen && hasChildren && (
                    <>
                        {children.map((childRow, index) => (
                            <PermissionChildActionRow
                                key={childRow.code}
                                className={index > 0 ? "mt-3" : ""}
                                currentChild={childRow}
                                moduleCode={moduleCode}
                            />
                        ))}
                    </>
                )}
            </div>

            {showScope && (
                <PermissionScope
                    moduleCode={moduleCode}
                    actionCode={action.code}
                    scope={scope}
                    isChildActive={isActive}
                    className="ps-2 ms-5"
                />
            )}
        </>
    );
};

export default React.memo(PermissionActionRow);
